// Public pages, the console, and staff admin are light surfaces.
const LIGHT_PREFIXES = [
  '/pricing',
  '/templates',
  '/client-work',
  '/signup',
  '/registration',
  '/passwords',
  '/pay/',
  '/a/',
  '/console',
  '/admin',
  '/connections',
];

function isMarketingPath(pathname: string): boolean {
  return pathname === '/' || LIGHT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isAuthPath(pathname: string): boolean {
  return pathname === '/login' || pathname.startsWith('/session');
}

function isWorkspacePath(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/');
}

export function applyThemeForPath(pathname: string): void {
  if (isMarketingPath(pathname) || isAuthPath(pathname)) {
    document.documentElement.classList.remove('dark');
    return;
  }

  if (isWorkspacePath(pathname)) {
    applyWorkspaceTheme(readWorkspaceTheme());
    return;
  }

  const savedTheme = localStorage.getItem('theme');

  if (savedTheme) {
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    return;
  }

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  document.documentElement.classList.toggle('dark', prefersDark);
}

export function initTheme() {
  applyThemeForPath(window.location.pathname);

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (isMarketingPath(window.location.pathname) || isAuthPath(window.location.pathname)) {
      document.documentElement.classList.remove('dark');
      return;
    }

    if (isWorkspacePath(window.location.pathname)) {
      applyWorkspaceTheme(readWorkspaceTheme());
      return;
    }

    if (!localStorage.getItem('theme')) {
      document.documentElement.classList.toggle('dark', e.matches);
    }
  });
}

export function setTheme(theme: 'light' | 'dark') {
  localStorage.setItem('theme', theme);

  if (isWorkspacePath(window.location.pathname)) {
    applyWorkspaceTheme(readWorkspaceTheme());
    return;
  }

  if (isMarketingPath(window.location.pathname) || isAuthPath(window.location.pathname)) {
    document.documentElement.classList.remove('dark');
    return;
  }

  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function getTheme() {
  return localStorage.getItem('theme');
}

export function isDarkTheme() {
  return getTheme() === 'dark';
}

export type WorkspaceTheme = 'dark' | 'light' | 'system';

const WORKSPACE_THEME_KEY = 'linear-theme';

export function readWorkspaceTheme(): WorkspaceTheme {
  const value = localStorage.getItem(WORKSPACE_THEME_KEY);
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  return 'dark';
}

export function applyWorkspaceTheme(theme: WorkspaceTheme): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.setAttribute('data-linear-theme', dark ? 'dark' : 'light');
}

export function setWorkspaceTheme(theme: WorkspaceTheme): void {
  localStorage.setItem(WORKSPACE_THEME_KEY, theme);
  applyWorkspaceTheme(theme);
  window.dispatchEvent(new Event('caprock:theme'));
}
